import {
  arrayEqualIgnoreOrder,
  booleanEqual,
  nullishEqual,
  numberEqual,
  objectEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";
import {
  AddressPropertiesSegment,
  CartValueSegment,
  CheckoutProbabilitySegment,
  CheckoutTreeCheckoutExperienceNodeOptionalId,
  CheckoutTreeSplitTestNodeOptionalId,
  CrossBorderShipmentSegment,
  CustomerReturnRateSegment,
  CustomerTagsSegment,
  DatetimeRangeSegment,
  DeliveryDistanceMilesSegment,
  DeliveryLocationCondition,
  DeliveryLocationNarySegment,
  DeliveryLocationRule,
  DeliveryLocationSegment,
  ItemQuantitySegment,
  LineItemPropertiesSegment,
  OrderCountSegment,
  OriginLocationSegment,
  PoBoxShipmentSegment,
  ProductCollectionsSegment,
  ProductMetafieldsSegment,
  ProductSkuSegment,
  ProductTagsSegment,
  ProductVendorSegment,
  TimeOfDaySegment,
} from "./checkout-tree";

export const checkoutExperienceNodeEqual =
  objectEqual<CheckoutTreeCheckoutExperienceNodeOptionalId>({
    nodeType: stringEqual,
    checkoutExperienceId: stringEqual,
  });

export const splitTestNodeEqual = objectEqual<
  Omit<CheckoutTreeSplitTestNodeOptionalId, "edges">
>({ nodeType: stringEqual, splitTestId: stringEqual });

export const deliveryDistanceMilesSegmentEqual =
  objectEqual<DeliveryDistanceMilesSegment>({
    segmentableCharacteristic: stringEqual,
    miles: numberEqual,
    operator: stringEqual,
  });

export const orderCountSegmentEqual = objectEqual<OrderCountSegment>({
  segmentableCharacteristic: stringEqual,
  numOrders: numberEqual,
  operator: stringEqual,
});

export const customerReturnRateSegmentEqual =
  objectEqual<CustomerReturnRateSegment>({
    segmentableCharacteristic: stringEqual,
    returnRatePercentage: numberEqual,
    operator: stringEqual,
  });

export const deliveryLocationSegmentEqual =
  objectEqual<DeliveryLocationSegment>({
    segmentableCharacteristic: stringEqual,
    locationCountry: objectEqual({
      countries: arrayEqualIgnoreOrder(),
      operator: stringEqual,
    }),
    locationProvince: optionalEqual(
      objectEqual({
        provinces: arrayEqualIgnoreOrder(),
        operator: stringEqual,
      }),
    ),
  });

export const customerTagsSegmentEqual = objectEqual<CustomerTagsSegment>({
  segmentableCharacteristic: stringEqual,
  tags: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  customerTagsSegmentNodeIdentifier: stringEqual,
});

export const productTagsSegmentEqual = objectEqual<ProductTagsSegment>({
  segmentableCharacteristic: stringEqual,
  tags: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  cartOperator: stringEqual,
  productTagsSegmentNodeIdentifier: stringEqual,
});

export const productVendorSegmentEqual = objectEqual<ProductVendorSegment>({
  segmentableCharacteristic: stringEqual,
  vendors: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  cartOperator: stringEqual,
});

export const productSkuSegmentEqual = objectEqual<ProductSkuSegment>({
  segmentableCharacteristic: stringEqual,
  skus: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  cartOperator: stringEqual,
  requireExactMatch: booleanEqual,
});

export const itemQuantitySegmentEqual = objectEqual<ItemQuantitySegment>({
  segmentableCharacteristic: stringEqual,
  quantity: numberEqual,
  operator: stringEqual,
  exclusions: optionalEqual(
    objectEqual<ItemQuantitySegment["exclusions"]>({
      skus: optionalEqual(arrayEqualIgnoreOrder()),
    }),
  ),
});

export const originLocationSegmentEqual = objectEqual<OriginLocationSegment>({
  segmentableCharacteristic: stringEqual,
  originAddress: objectEqual<OriginLocationSegment["originAddress"]>({
    address1: nullishEqual(stringEqual),
    address2: nullishEqual(stringEqual),
    city: nullishEqual(stringEqual),
    province: nullishEqual(stringEqual),
    postalCode: nullishEqual(stringEqual),
    country: nullishEqual(stringEqual),
  }),
});

export const timeOfDaySegmentEqual = objectEqual<TimeOfDaySegment>({
  segmentableCharacteristic: stringEqual,
  time: objectEqual({ hh: numberEqual, mm: numberEqual }),
  ianaTimezone: stringEqual,
  operator: stringEqual,
  weekdays: arrayEqualIgnoreOrder(),
});

export const datetimeRangeSegmentEqual = objectEqual<DatetimeRangeSegment>({
  segmentableCharacteristic: stringEqual,
  startDateTime: stringEqual,
  endDateTime: stringEqual,
  ianaTimezone: stringEqual,
});

export const checkoutProbabilityExpressionEqual = arrayEqualIgnoreOrder<
  CheckoutProbabilitySegment["expressions"][number]
>((expr) => `${expr.probability}:${expr.operator}`);

export const checkoutProbabilitySegmentEqual =
  objectEqual<CheckoutProbabilitySegment>({
    segmentableCharacteristic: stringEqual,
    expressions: checkoutProbabilityExpressionEqual,
  });

export const productCollectionsSegmentEqual =
  objectEqual<ProductCollectionsSegment>({
    segmentableCharacteristic: stringEqual,
    collectionIds: arrayEqualIgnoreOrder(),
    collectionListOperator: stringEqual,
    cartOperator: stringEqual,
  });

export const lineItemPropertiesSegmentEqual =
  objectEqual<LineItemPropertiesSegment>({
    segmentableCharacteristic: stringEqual,
    properties: arrayEqualIgnoreOrder(
      (prop) => `${prop.key}:${prop.value}:${prop.matchMode ?? "exact"}`,
    ),
    propertiesListOperator: stringEqual,
    cartOperator: stringEqual,
  });

export const productMetafieldsSegmentEqual =
  objectEqual<ProductMetafieldsSegment>({
    segmentableCharacteristic: stringEqual,
    metafields: arrayEqualIgnoreOrder(
      (metafield) =>
        `${metafield.resourceType}:${metafield.namespace}.${metafield.key}=${metafield.value}:${metafield.valueMatchKind || "exact"}`,
    ),
    operator: stringEqual,
    cartOperator: stringEqual,
  });

export const cartValueSegmentEqual = objectEqual<CartValueSegment>({
  segmentableCharacteristic: stringEqual,
  value: objectEqual({ amount: numberEqual }),
  operator: stringEqual,
  valueKind: stringEqual,
});

export const crossBorderShipmentSegmentEqual =
  objectEqual<CrossBorderShipmentSegment>({
    segmentableCharacteristic: stringEqual,
  });

export const poBoxShipmentSegmentEqual = objectEqual<PoBoxShipmentSegment>({
  segmentableCharacteristic: stringEqual,
});

export const addressPropertiesSegmentEqual =
  objectEqual<AddressPropertiesSegment>({
    segmentableCharacteristic: stringEqual,
    rules: arrayEqualIgnoreOrder<AddressPropertiesSegment["rules"][number]>(
      (rule) => `${rule.field}:${rule.operator}:${rule.value}`,
    ),
  });

export const deliveryLocationRuleEqual = objectEqual<DeliveryLocationRule>({
  countryCode: stringEqual,
  provinces: optionalEqual(arrayEqualIgnoreOrder()),
});

export const deliveryLocationConditionEqual =
  objectEqual<DeliveryLocationCondition>({
    edgeId: stringEqual,
    locationRules: arrayEqualIgnoreOrder<DeliveryLocationRule>(
      (rule) =>
        `${rule.countryCode}:${(rule.provinces ?? []).sort().join(",")}`,
    ),
  });

export const deliveryLocationNarySegmentEqual =
  objectEqual<DeliveryLocationNarySegment>({
    segmentableCharacteristic: stringEqual,
    conditions: arrayEqualIgnoreOrder<DeliveryLocationCondition>(
      (condition) =>
        `${condition.edgeId}:${condition.locationRules
          .map(
            (r) => `${r.countryCode}:${(r.provinces ?? []).sort().join(",")}`,
          )
          .sort()
          .join(";")}`,
    ),
  });
